import { gtmSendEvent } from "@wojo/analytics";
import {
    Button,
    Container,
    StatusLabel,
    StatusLabelStatus,
    WistiaApi,
} from "@wojo/ui";
import cn from "clsx";
import React, { HtmlHTMLAttributes, forwardRef, useState } from "react";
import { useTourPageContext } from "../../_client/context";
import { CollageImage } from "./CollageImage";
import { CollageLayout } from "./CollageLayout";
import { TourBreadcrumb } from "./TourBreadcrumb";
import styles from "./TourHero.module.scss";
import { TourHeroSummary } from "./TourHeroSummary";
import { TourMediaSliderModal } from "./TourMediaSliderModal";
import { TourVideoPlayer } from "./TourVideoPlayer";

declare const window: Window & {
    Wistia?: WistiaApi;
};

export const TourHero = forwardRef<
    HTMLElement,
    HtmlHTMLAttributes<HTMLElement>
>((_, ref) => {
    const { colorPalette, tourContent, ancestors } = useTourPageContext();
    const [openMediaSlider, setOpenMediaSlider] = useState(false);
    const [startIndex, setStartIndex] = useState<number>();
    const backgroundImage = `var(--s-gradient-${colorPalette}-quiet)`;
    const tourBadge = tourContent.badge;

    const assets = tourContent.assets;

    const handleHeroImageClick = () => {
        gtmSendEvent({
            event: "track",
            ga4Tracking: {
                ga4_object: "MODAL",
                ga4_action: "OPENED",
                modalId: "TourMediaSlider",
            },
        });

        if (assets[0]?.__typename === "TourVideo" && assets[0]?.wistiaVideoId) {
            const video = window.Wistia?.api(assets[0].wistiaVideoId);
            video?.pause();
        }

        setOpenMediaSlider(true);
    };

    return (
        <section
            className={styles["tour-hero"]}
            style={
                {
                    "--hero-background-image": backgroundImage,
                } as React.CSSProperties
            }
            ref={ref}
        >
            <Container
                className={styles["tour-hero-container"]}
                spacing={{
                    xs: "0",
                }}
            >
                {openMediaSlider ? (
                    <TourMediaSliderModal
                        startIndex={startIndex}
                        open={openMediaSlider}
                        onCloseRequested={() => setOpenMediaSlider(false)}
                    />
                ) : null}
                <TourBreadcrumb
                    ancestors={ancestors}
                    tourName={tourContent.name}
                />
                <CollageLayout
                    content={<TourHeroSummary />}
                    media1={
                        <>
                            {!!tourBadge?.text && (
                                <StatusLabel
                                    className={styles.badge}
                                    variant="primary"
                                    size="standard"
                                    status={
                                        (tourBadge.variant ||
                                            "urgent") as StatusLabelStatus
                                    }
                                    text={tourBadge.text}
                                />
                            )}
                            {assets[0]?.__typename === "TourVideo" &&
                            assets[0]?.wistiaVideoId ? (
                                <TourVideoPlayer
                                    wistiaVideoId={assets[0].wistiaVideoId}
                                />
                            ) : (
                                assets[0]?.__typename === "TourImage" &&
                                assets[0]?.image && (
                                    <CollageImage
                                        {...assets[0].image}
                                        height={1100}
                                        width={700}
                                        defer={false}
                                        onClick={() => {
                                            setStartIndex(0);
                                            handleHeroImageClick();
                                        }}
                                    />
                                )
                            )}
                        </>
                    }
                    media2={
                        assets[1]?.__typename === "TourImage" &&
                        assets[1]?.image && (
                            <CollageImage
                                {...assets[1].image}
                                height={600}
                                width={600}
                                onClick={() => {
                                    setStartIndex(1);
                                    handleHeroImageClick();
                                }}
                            />
                        )
                    }
                    media3={
                        assets[2]?.__typename === "TourImage" &&
                        assets[2]?.image && (
                            <>
                                <CollageImage
                                    {...assets[2].image}
                                    height={600}
                                    width={500}
                                    onClick={() => {
                                        setStartIndex(2);
                                        handleHeroImageClick();
                                    }}
                                />
                                <SeeAllButton
                                    className={styles["see-all-button-mobile"]}
                                    onClick={() => {
                                        setStartIndex(0);
                                        handleHeroImageClick();
                                    }}
                                >
                                    {`See all (${assets.length})`}
                                </SeeAllButton>
                            </>
                        )
                    }
                    media4={
                        assets[3]?.__typename === "TourImage" &&
                        assets[3]?.image && (
                            <>
                                <CollageImage
                                    {...assets[3].image}
                                    height={600}
                                    width={800}
                                    onClick={() => {
                                        setStartIndex(3);
                                        handleHeroImageClick();
                                    }}
                                />
                                <SeeAllButton
                                    className={styles["see-all-button-desktop"]}
                                    onClick={() => {
                                        setStartIndex(0);
                                        handleHeroImageClick();
                                    }}
                                >
                                    {`See all (${assets.length})`}
                                </SeeAllButton>
                            </>
                        )
                    }
                />
            </Container>
        </section>
    );
});
TourHero.displayName = "TourHero";

const SeeAllButton: React.FC<{
    onClick: () => void;
    children: React.ReactNode;
    className?: string;
}> = ({ onClick, children, className }) => {
    return (
        <Button
            className={cn(styles["see-all-button"], className)}
            palette="monochrome"
            size="small"
            variant="inverse"
            onClick={onClick}
        >
            {children}
        </Button>
    );
};
